import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import moment from "moment";
import toast from "react-hot-toast";
import Select from "react-select";
//hisabkitab.css
import "../../styles/hisabkitab.css";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import { AuthContext } from "../../context/AuthContext";
import { selectCustomStyles } from "../../utils/utils";
import WalletTransferServices from "../../services/WalletTransferServices";
import HisabKitabServices from "../../services/HisabKitabServices";

const HisabKitabManage = () => {
  const walletTransferApi = useMemo(() => new WalletTransferServices(), []);
  const hisabKitabApi = useMemo(() => new HisabKitabServices(), []);

  const { user } = useContext(AuthContext);
  const [userId, setUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dropdownUsers, setDropdownUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hisabKitabList, setHisabKitabList] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [from, setFrom] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));

  const hisabKitabSelectStyles = useMemo(
    () => ({
      ...selectCustomStyles,
      control: (base, state) => ({
        ...(typeof selectCustomStyles.control === "function"
          ? selectCustomStyles.control(base, state)
          : base),
        minWidth: "min(100%, 320px)",
        maxWidth: "100%",
        borderRadius: "10px",
        borderColor: "#fff",
        minHeight: "42px",
      }),
    }),
    [],
  );
  const userOptions = useMemo(() => {
    const selfId = Number(user?.user_id);
    return (dropdownUsers || [])
      .filter((u) => Number(u.id) !== selfId)
      .map((u) => {
        const id = Number(u.id);
        const uname = u.username || "—";
        return {
          value: id,
          label: `${uname}`,
          raw: u,
        };
      });
  }, [dropdownUsers, user?.user_id]);

  /** Newest calendar day first; ISO date strings sort chronologically. */
  const displayHisabRows = useMemo(() => {
    const rows = [...(hisabKitabList || [])];
    rows.sort((a, b) =>
      String(b.date || "").localeCompare(String(a.date || "")),
    );
    return rows;
  }, [hisabKitabList]);

  const formatHisabDate = useCallback((isoDate) => {
    if (isoDate == null || isoDate === "") return "—";
    const d = String(isoDate).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return "—";
    return moment.utc(d, "YYYY-MM-DD").format("DD-MM-YYYY");
  }, []);

  /** e.g. [+100000, +5000, -2000] — positives always show explicit + */
  const formatTransferredList = useCallback((amounts) => {
    if (!Array.isArray(amounts) || amounts.length === 0) return "—";
    const parts = amounts.map((n) => {
      const x = Number(n);
      if (!Number.isFinite(x)) return "+0";
      if (x > 0) return `+${x}`;
      if (x < 0) return String(x);
      return "+0";
    });
    return `[${parts.join(",")}]`;
  }, []);

  const loadHisabKitabData = async () => {
    if(!userId) return;
    setLoading(true);
    try {
      const res = await hisabKitabApi.getHisabKitabData({
        page,
        limit,
        q,
        from,
        to,
        userId,
      });
      if (res.isSuccess) {
        setHisabKitabList(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load hisab kitab data");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load hisab kitab data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHisabKitabData();
  }, [page, limit, q, from, to, userId]);

  const getDropdownUsers = useCallback(async () => {
    try {
      const res = await walletTransferApi.getDropdownUsers();
      if (res.isSuccess) {
        setDropdownUsers(res.data || []);
      } else {
        toast.error(res.message || "Failed to load users");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load users");
    }
  }, [walletTransferApi]);

  const onFrom = (e) => {
    setFrom(e.target.value);
  };

  const onTo = (e) => {
    setTo(e.target.value);
  };

  useEffect(() => {
    getDropdownUsers();
  }, [getDropdownUsers]);

  return (
    <div>
      <div className="hisab-kitab-header">
        <div>
          <div className="hisab-kitab-search">
            <Select
              inputId="hisab-kitab-user-select"
              instanceId="hisab-kitab-user"
              className="hisab-kitab-user-select"
              classNamePrefix="hk-select"
              styles={hisabKitabSelectStyles}
              options={userOptions}
              value={selectedUser}
              onChange={(opt) => {
                setSelectedUser(opt);
                setPage(1);
                setUserId(opt?.value ?? null);
                setHisabKitabList([]);
                setPagination(null);
              }}
              placeholder="Search by name, username, or ID…"
              isClearable
              isSearchable
              filterOption={(candidate, input) => {
                if (!input) return true;
                const q = input.toLowerCase().trim();
                const r = candidate.data?.raw;
                if (!r)
                  return String(candidate.label).toLowerCase().includes(q);
                const hay = [r.name, r.username, String(r.id)]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase();
                return hay.includes(q);
              }}
              noOptionsMessage={() => "No users match"}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
            />
          </div>
        </div>
        <div className="hisab-kitab-filters-container">
          <div className="hisab-kitab-filters">
            <div className="hisab-kitab-date">
              <label>From</label>
              <input type="date" value={from} onChange={onFrom} />
            </div>
            <div className="hisab-kitab-date">
              <label>To</label>
              <input type="date" value={to} onChange={onTo} />
            </div>
          </div>
        </div>
      </div>

      {!selectedUser ? (
        <NoData
          message="No User Selected"
          subText="Select a user to view hisab kitab."
        />
      ) : (
        <>
          <div>
            {loading ? (
              <Loader message="Loading hisab kitab..." />
            ) : hisabKitabList.length === 0 ? (
              <NoData
                message="No hisab kitab found"
                subText="Try changing date range or select another user."
              />
            ) : (
              <>
                <div className="hk-table-scroll">
                  <div className="hk-table-header">
                    <div>ID</div>
                    <div>Name</div>
                    <div>Transferred Amount</div>
                    <div>Total Hisab</div>
                    <div>Date</div>
                  </div>

                  <div className="hk-table-body">
                    {displayHisabRows.map((r) => {
                      const total = Number(r.totalHisab ?? 0);
                      const totalClass =
                        total > 0
                          ? "hk-total-pos"
                          : total < 0
                            ? "hk-total-neg"
                            : "hk-total-zero";
                      return (
                        <div
                          className="hk-table-row"
                          key={r.rowKey || `${r.userId}-${r.date}`}
                        >
                          <div>{r.displayId ?? "—"}</div>
                          <div>{r.userName ?? "—"}</div>
                          <div className="hk-amounts-cell">
                            {formatTransferredList(r.transferredAmounts)}
                          </div>
                          <div className={totalClass}>
                            {total.toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </div>
                          <div>{formatHisabDate(r.date)}</div>
                        </div>
                      );
                    })}
                  </div>

                  {pagination && pagination.total > 0 && (
                    <div className="modern-pagination">
                      {/* Limit Selector */}
                      <div className="mp-limit">
                        <span>Show</span>
                        <select
                          value={limit}
                          onChange={(e) =>
                            setLimit(Number(e.target.value) || 10)
                          }
                        >
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                          <option value="200">200</option>
                          <option value="500">500</option>
                          <option value="1000">1000</option>
                        </select>
                      </div>

                      {/* Navigation */}
                      <div className="mp-controls">
                        <button
                          disabled={page === 1 || pagination.totalPages <= 1}
                          onClick={() => setPage(page - 1)}
                        >
                          Prev
                        </button>

                        <div className="mp-info">
                          Page <strong>{pagination.page}</strong> of{" "}
                          <strong>{pagination.totalPages}</strong>
                        </div>

                        <button
                          disabled={
                            page === pagination.totalPages ||
                            pagination.totalPages <= 1
                          }
                          onClick={() => setPage(page + 1)}
                        >
                          Next
                        </button>
                      </div>

                      {pagination.totalPages > 1 && (
                        <div className="mp-jump">
                          <select
                            value={page}
                            onChange={(e) =>
                              setPage(Number(e.target.value) || 1)
                            }
                          >
                            {Array.from(
                              { length: pagination.totalPages },
                              (_, i) => (
                                <option key={i + 1} value={i + 1}>
                                  Page {i + 1}
                                </option>
                              ),
                            )}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default HisabKitabManage;
